export const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export const formatText = (value, maxLength = 40) => {
  if (value === null || value === undefined || value === "") return "-";
  const text = String(value);
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatLongDate = (value) => {
  const date = toDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

export const formatLongDateTime = (value) => {
  const date = toDate(value);
  if (!date) return "-";
  const dateText = formatLongDate(date);
  const timeText = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
  return `${dateText}, ${timeText}`;
};

export const statusColor = (status) => {
  if (!status) return "default";
  const value = String(status).toUpperCase();
  if (["ACTIVE", "OPEN", "NEW", "APPROVED", "AVAILABLE"].includes(value)) return "green";
  if (["DRAFT", "PENDING", "PAUSED"].includes(value)) return "gold";
  if (["SUSPENDED", "CLOSED", "DELETED", "CANCELLED", "EXPIRED", "FILLED"].includes(value)) return "red";
  return "blue";
};

export const tagStatusText = (status) => {
  if (status === null || status === undefined || status === "") return "-";
  return String(status).replaceAll("_", " ");
};

export const enumOptions = (values = []) =>
  values.map((value) => ({
    value,
    label: String(value).replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase()),
  }));

export const csvToArray = (value) => {
  if (!value) return [];
  return String(value)
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

export const arrayToCsv = (value) => (Array.isArray(value) ? value.join(", ") : "");

export const jsonToPrettyString = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const formatAnswerOptions = (value) => {
  if (value === null || value === undefined || value === "") return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === null || item === undefined || item === "") return null;
        if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
          return String(item);
        }
        if (typeof item === "object") {
          const label = item.label || item.name || item.title || item.value || item.code;
          if (label) return String(label);
          return Object.entries(item)
            .map(([key, val]) => `${key}: ${val}`)
            .join(", ");
        }
        return String(item);
      })
      .filter(Boolean);
  }
  if (typeof value === "object") {
    return Object.entries(value).map(([key, val]) => `${key}: ${val}`);
  }
  return [String(value)];
};

export const normalizeAnswerOptionsForForm = (value) => formatAnswerOptions(value);

export const normalizeAnswerOptionsForPayload = (value) => {
  if (!Array.isArray(value)) return null;
  const cleaned = value.map((item) => String(item).trim()).filter(Boolean);
  return cleaned.length ? cleaned : null;
};

export const parseJsonMaybe = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const formatLocations = (value) => {
  if (!value) return null;
  let arr = value;
  if (typeof value === "string") {
    try {
      arr = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(arr) || !arr.length) return null;
  return arr
    .map((loc) => {
      if (!loc || typeof loc !== "object") return null;
      if (loc.anyLocation) return "Any location";
      const parts = [loc.city, loc.state, loc.country].filter(Boolean);
      return parts.length ? parts.join(", ") : "-";
    })
    .filter(Boolean)
    .join("; ");
};

export const formatLocationsForForm = (value) => {
  if (!value) return [];
  let arr = value;
  if (typeof value === "string") {
    try {
      arr = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr) || !arr.length) return [];
  return arr
    .map((loc) => {
      if (!loc || typeof loc !== "object") return null;
      if (loc.anyLocation) return "Any location";
      const parts = [loc.city, loc.state, loc.country].filter(Boolean);
      return parts.length ? parts.join(", ") : null;
    })
    .filter(Boolean);
};

export const parseLocations = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .map((item) => {
        if (item.toLowerCase() === "any location") return { anyLocation: true };
        const segments = item.split(",").map((s) => s.trim()).filter(Boolean);
        const loc = {};
        if (segments[0]) loc.city = segments[0];
        if (segments.length > 1) loc.state = segments[1];
        if (segments.length > 2) loc.country = segments[2];
        return loc;
      })
      .filter(Boolean);
  }
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "[]") return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return trimmed
    .split(";")
    .map((part) => {
      const text = part.trim();
      if (!text) return null;
      if (text.toLowerCase() === "any location") return { anyLocation: true };
      const segments = text.split(",").map((s) => s.trim()).filter(Boolean);
      const loc = {};
      if (segments[0]) loc.city = segments[0];
      if (segments.length > 1) loc.state = segments[1];
      if (segments.length > 2) loc.country = segments[2];
      return loc;
    })
    .filter(Boolean);
};

export const normalizeSelectionValue = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === null || value === undefined || value === "") return [];
  return [value].filter(Boolean);
};
